import request from '@/axios/config'
import { myTasksRequest } from '@/axios/myTasksConfig'
import type { MyTasksApiResponse, TaskEntry } from '@/pages/MyTasks/types'
import { isAwaitingContactTask } from '@/pages/MyTasks/utils/handoffTasks'
import { partitionSuppressedTasks } from '@/pages/MyTasks/utils/suppressedTasks'
import { getTimeIndicatorData } from '@/pages/MyTasks/utils/timeIndicatorHelpers'
import type { AssistantBriefing, AssistantNudge } from './nudge'

/** The same switches My Tasks reads (MyTasksDataContext). */
export interface NudgeSourceOptions {
  /** `FeatureFlag.MY_CASES_V4_5` */
  isV45: boolean
  /** `FeatureFlag.WITHHOLD_CLIENT_CONTACT_ENABLED` */
  withholdClientContactEnabled: boolean
  /** The signed-in user, for the card's buyer/seller time wording. */
  userId?: string
}

/**
 * Urgent rows asked for. More than one, so a suppressed or informational head
 * row (dropped below) cannot hide the next visible urgent task.
 */
export const NUDGE_URGENT_LIMIT = 5

/** "Respond to Invitation" -> "Respond to invitation" */
const toSentenceCase = (label: string) =>
  label.charAt(0).toUpperCase() + label.slice(1).toLowerCase()

/**
 * The card's shorthand spelled out, here only (the shared My Tasks helper
 * keeps its "hs"): "5 hs" -> "5 hours", "1 hr" -> "1 hour", "0 hs" -> "under
 * an hour", "30 mins" -> "30 minutes". Days and months are already words.
 */
export const spellOutNudgeTime = (text: string) =>
  text
    .replace(/\b0 (?:hs|hrs?|hours?)\b/g, 'under an hour')
    .replace(/\b1 (?:hs|hrs?)\b/g, '1 hour')
    .replace(/\b(\d+) (?:hs|hrs)\b/g, '$1 hours')
    .replace(/\b1 mins?\b/g, '1 minute')
    .replace(/\b(\d+) mins?\b/g, '$1 minutes')

/**
 * The time the My Tasks card shows for this task (`getTimeIndicatorData`),
 * lower-cased and spelled out: "expires in 5 hours", "3 days remaining",
 * "requested 2 days ago", and whether the card shows it as urgent (red).
 * Nothing when the card shows nothing (public proposals, expired, no
 * deadline).
 */
export const nudgeDueIndicator = (task: TaskEntry, userId = '') => {
  const actionType = task.urgency_metadata?.action_type
  if (!actionType) return null
  const indicator = getTimeIndicatorData(task, actionType, userId)
  if (!indicator?.text) return null
  return {
    text: spellOutNudgeTime(indicator.text.toLowerCase()),
    urgent: indicator.color === 'error',
  }
}

export const formatNudgeDue = (task: TaskEntry, userId = '') =>
  nudgeDueIndicator(task, userId)?.text ?? null

/** Longest case title the nudge sentence names; the briefing shows it all. */
export const NUDGE_TITLE_MAX = 60

export const capNudgeTitle = (title: string) =>
  title.length > NUDGE_TITLE_MAX
    ? `${title.slice(0, NUDGE_TITLE_MAX - 1).trimEnd()}…`
    : title

/** The task types whose card shows the other firm rather than the case. */
const FIRM_ACTIONS = new Set([
  'escrow_alert',
  'contact_client',
  'in_progress',
  'update_status_requested',
  'update_status_cron',
])

/**
 * What the My Tasks card shows besides the time: the other firm for
 * contact and status tasks, practice area and state otherwise. Never client
 * contact details, and never the case fee (the card doesn't show it).
 */
const briefingFacts = (task: TaskEntry): string[] => {
  const actionType = task.urgency_metadata?.action_type ?? ''
  const where = FIRM_ACTIONS.has(actionType)
    ? task.attorney?.organization_name?.trim()
    : [task.type, task.jurisdiction].filter(Boolean).join(' · ')
  // The My Tasks card never shows the case's referral fee, so neither do we.
  return where ? [where] : []
}

/** The second follow-up, where one fits the task. */
const RESPOND_FOLLOW_UPS: Record<string, { label: string; ask: string }> = {
  accept_decline_invitation: {
    label: 'Help me respond',
    ask: 'Help me respond to the invitation on',
  },
  respond_proposal: {
    label: 'Help me respond',
    ask: 'Help me respond to the proposal on',
  },
  respond_counteroffer: {
    label: 'Help me respond',
    ask: 'Help me respond to the counteroffer on',
  },
  data_room_request: {
    label: 'Help me respond',
    ask: 'Help me respond to the data room request on',
  },
  update_status_requested: {
    label: 'Update the status',
    ask: 'Help me update the status of',
  },
  update_status_cron: {
    label: 'Update the status',
    ask: 'Help me update the status of',
  },
}

/**
 * One urgent task as the nudge's copy and the briefing Review opens. Only the
 * case title is named, as on the My Tasks card; nothing about the client.
 */
export const buildAssistantNudge = (
  task: TaskEntry | null | undefined,
  userId = ''
): AssistantNudge | null => {
  if (!task?.id) return null
  const meta = task.urgency_metadata
  const actionType = meta?.action_type ?? 'task'
  const action = meta?.action_label?.trim()
    ? toSentenceCase(meta.action_label.trim())
    : 'Action needed'
  const title = task.name?.trim() ?? ''
  const dueIndicator = nudgeDueIndicator(task, userId)
  const due = dueIndicator?.text ?? null
  const subject = title ? ` on "${capNudgeTitle(title)}"` : ''
  // The case id lets the assistant open the right case (get_case) even when
  // titles repeat. It goes with the message as context (focusCaseId), never
  // in the text the user sees.
  const caseRef = title ? `"${title}"` : 'my most urgent task'
  const question = `What do I need to do on ${caseRef}?`
  const respond = RESPOND_FOLLOW_UPS[actionType]
  const briefing: AssistantBriefing = {
    caseId: task.id,
    title,
    actionType,
    actionLabel: action,
    due,
    dueUrgent: Boolean(dueIndicator?.urgent),
    facts: briefingFacts(task),
    followUps: [
      { label: 'What should I do next?', question, caseId: task.id },
      ...(respond
        ? [
            {
              label: respond.label,
              question: `${respond.ask} ${caseRef}.`,
              caseId: task.id,
            },
          ]
        : []),
    ],
  }
  return {
    id: `${task.id}:${actionType}`,
    message: `${action}${subject}${due ? ` — ${due}` : ''}.`,
    question,
    caseId: task.id,
    briefing,
  }
}

/**
 * The first urgent task My Tasks would show in Urgent: after its suppression
 * rules, and without the buyer's informational "Awaiting Contact Info" (My
 * Tasks lists it below Next Up; there is nothing to do yet).
 */
export const pickNudgeTask = (
  response: MyTasksApiResponse | undefined,
  { withholdClientContactEnabled }: NudgeSourceOptions
): TaskEntry | null =>
  partitionSuppressedTasks(response?.urgent?.data || [], {
    withholdClientContactEnabled,
  }).visibleItems.find(task => !isAwaitingContactTask(task)) ?? null

/**
 * My Tasks' needs-action endpoint, small: the urgency view with a few urgent
 * rows and one next-up row. Errors resolve to null: the nudge is optional.
 */
export const fetchAssistantNudge = async (
  options: NudgeSourceOptions
): Promise<AssistantNudge | null> => {
  const axiosInstance = options.isV45 ? myTasksRequest : request
  const endpoint = options.isV45
    ? '/my-referrals/v4/needs-action'
    : '/v4/myreferrals/needs-action'
  try {
    const response = await axiosInstance.get<MyTasksApiResponse>(endpoint, {
      params: {
        use_urgency_view: 'true',
        urgent_limit: String(NUDGE_URGENT_LIMIT),
        next_up_limit: '1',
      },
    })
    return buildAssistantNudge(
      pickNudgeTask(response?.data, options),
      options.userId
    )
  } catch {
    return null
  }
}
